import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { ArrowRight, Camera, Compass, FileUp, MapPin, Navigation, PenLine, Plus, Send, Wallet, Wifi, CalendarClock } from 'lucide-react';
import { useElioDrawer } from '../components/elio/ElioDrawer';
import { useApp } from '../data/AppProvider';
import { useMe, useTrip, useTrips } from '../data/hooks';
import { eventsOnDay, nextEvent, pickActiveTrip, primaryActionFor, type DayMode } from '../domain/now';
import type { EventWithPeople, TripStop } from '../domain/model';
import { deviceTimeZone, formatDate, formatDuration, localDateOf, zoneAbbreviation } from '../domain/time';
import { Badge, Button, Card, Input, Notice, SectionHeader, Spinner, Tabs, cx } from '../components/ui';
import { AttentionList, CategoryIcon, EventTime, FactBadge, TripDates, eventFactState, useFindings } from '../components/trip';
import { Photo, slotForPlaces } from '../lib/imagery';

function currentStop(stops: TripStop[], today: string): TripStop | null {
  return stops.find((s) => s.arrive_on && s.arrive_on <= today && (!s.depart_on || today < s.depart_on)) ?? stops.find((s) => s.arrive_on === today) ?? null;
}

function useClock(intervalMs = 30_000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(t);
  }, [intervalMs]);
  return now;
}

function readStart(): string {
  try { return window.localStorage.getItem('elio-start-point') ?? ''; } catch { return ''; }
}

export function NowScreen() {
  const { session, scope, activeTripOverride, setActiveTripOverride } = useApp();
  const elio = useElioDrawer();
  const trips = useTrips();
  const me = useMe();
  const now = useClock();
  const navigate = useNavigate();
  const homeTz = me.data?.settings.home_timezone ?? deviceTimeZone() ?? 'UTC';

  const scoped = useMemo(() => (trips.data ?? []).filter((t) => t.myStatus === 'accepted' && (scope.kind === 'personal' || t.group_id === scope.groupId)), [trips.data, scope]);
  const invitations = (trips.data ?? []).filter((t) => t.myStatus === 'invited');
  const today = localDateOf(now, homeTz);
  const active = pickActiveTrip(scoped, today, activeTripOverride);
  const bundle = useTrip(active.trip?.id);
  const isGroupTrip = Boolean(active.trip?.group_id);
  const [mode, setMode] = useState<DayMode>(scope.kind === 'group' ? 'group' : 'my');
  useEffect(() => setMode(scope.kind === 'group' ? 'group' : 'my'), [scope]);

  if (trips.isLoading) return <Spinner label="Loading your trips" />;
  if (trips.error) return <Notice tone="danger" title="We couldn't load your trips">Check your connection and try again.</Notice>;

  if (!active.trip) {
    return (
      <div className="mx-auto max-w-4xl">
        {invitations.length > 0 && <InvitationsBanner count={invitations.length} />}
        <Card className="overflow-hidden shadow-sm">
          <Photo slot="coast" width={1400} overlay="left" priority className="min-h-64 sm:min-h-72">
            <div className="flex flex-1 flex-col justify-end px-6 pb-7 pt-10 text-white sm:px-8">
              <p className="text-sm text-white/85">{scope.kind === 'group' ? scope.name : `Welcome${me.data ? `, ${me.data.profile.display_name}` : ''}`}</p>
              <h1 className="mt-1 font-editorial text-4xl leading-tight text-white drop-shadow sm:text-5xl">{scope.kind === 'group' ? 'No shared trip yet' : 'Where to next?'}</h1>
              <p className="mt-2 max-w-md text-white/90">Start however suits you — nothing is booked or shared until you say so.</p>
            </div>
          </Photo>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,14rem),1fr))] gap-3 p-4">
            <ChoiceCard to="/trips/new" icon={<Plus className="size-5" />} title="Plan a trip" text="Destination, rough dates and where you start." />
            <ChoiceCard to="/documents" icon={<FileUp className="size-5" />} title="Add my bookings" text="Upload tickets and confirmations, or enter them by hand." />
            <ChoiceCard to="/explore" icon={<Compass className="size-5" />} title="Explore ideas" text="Browse regions and check what your documents allow." />
          </div>
        </Card>
      </div>
    );
  }

  const trip = active.trip;
  const b = bundle.data;
  const stop = b ? currentStop(b.stops, today) : null;
  const tz = stop?.timezone ?? trip.origin_timezone ?? homeTz;
  const localToday = localDateOf(now, tz);
  const memberIds = (b?.members ?? []).filter((m) => m.status === 'accepted').map((m) => m.user_id);
  const effectiveMode: DayMode = isGroupTrip ? mode : 'my';
  const next = b ? nextEvent(b.events, now, { userId: session!.userId, mode: effectiveMode, tz }) : null;
  const focus = next?.inProgress ?? next?.next ?? null;
  const todays = b ? eventsOnDay(b.events.filter((e) => e.status !== 'canceled'), localToday, tz) : [];
  const todayFiltered = effectiveMode === 'my' ? todays.filter((e) => e.participant_ids.length === 0 ? (e.visibility !== 'subgroup' || e.created_by === session!.userId) : e.participant_ids.includes(session!.userId)) : todays;

  return (
    <div className="mx-auto grid max-w-[1240px] gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
      <div className="min-w-0 space-y-5">
        {invitations.length > 0 && <InvitationsBanner count={invitations.length} />}
        {/* Context banner — compact so the next action stays above the fold */}
        <Photo slot={slotForPlaces(stop?.city, trip.title, ...(b?.stops.map((x) => x.city) ?? []))} width={1400} overlay="left" priority className="min-h-40 rounded-3xl shadow-sm sm:min-h-44">
          <div className="flex flex-1 flex-col justify-end px-5 pb-4 pt-8 text-white sm:px-7 sm:pb-5">
            <p className="text-sm text-white/85">{active.phase === 'active' ? 'Travelling now' : active.phase === 'upcoming' ? 'Next trip' : 'Planning'}</p>
            <h1 className="font-editorial text-4xl leading-tight text-white drop-shadow break-words sm:text-5xl">
              <Link to={`/trips/${trip.id}`} className="text-white hover:underline">{trip.title}</Link>
            </h1>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white/90">
              <span className="tabular-nums"><span className="whitespace-nowrap">{new Intl.DateTimeFormat('en-GB', { timeZone: tz, weekday: 'short', day: 'numeric', month: 'short' }).format(now)},</span> <span className="whitespace-nowrap">{new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit' }).format(now)} {zoneAbbreviation(now, tz)}</span></span>
              {stop && <span><MapPin className="mr-0.5 inline size-3.5 align-[-2px]" aria-hidden />{stop.city}</span>}
              <span className="text-xs text-white/75">{tz}</span>
            </p>
          </div>
        </Photo>
        <div className="flex flex-wrap items-center justify-end gap-2 empty:hidden">
          <TripPicker trips={scoped} activeId={trip.id} overridden={active.overridden} onPick={setActiveTripOverride} />
        </div>
        {active.overlapping.length > 0 && (
          <Notice tone="warn" title="Another trip overlaps today">
            {active.overlapping.map((t) => t.title).join(', ')} also includes today. Choose which trip Now should follow.
          </Notice>
        )}

        {isGroupTrip && (
          <Tabs label="Whose day" value={mode} onChange={setMode} items={[{ value: 'my', label: 'My day' }, { value: 'group', label: 'Group day' }]} className="w-fit" />
        )}

        {/* Next important event */}
        {!b ? <Spinner label="Loading trip" /> : (
          <NextCard focus={focus} inProgress={Boolean(next?.inProgress)} minutes={next?.minutesUntilNext ?? null} tripId={trip.id} phase={active.phase} needsTimeCount={next?.needsTime.length ?? 0} />
        )}

        <AskInline tripTitle={trip.title} onSubmit={(text) => elio.open({ tripId: trip.id, text })} onPhoto={() => elio.open({ tripId: trip.id })} />

        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" icon={<Wallet className="size-4" />} onClick={() => navigate(`/trips/${trip.id}/budget`, { state: { addSpending: true } })}>Add spending</Button>
          <Button variant="secondary" icon={<FileUp className="size-4" />} onClick={() => navigate('/documents', { state: { tripId: trip.id } })}>Add a booking</Button>
        </div>

        {/* Mobile: Needs attention appears here, prominently */}
        <section aria-labelledby="attn-m" className="xl:hidden">
          <SectionHeader id="attn-m" title="Needs attention" />
          {b && <NowAttention bundle={b} memberIds={memberIds} mode={effectiveMode} />}
        </section>

        {/* Today */}
        <section aria-labelledby="today">
          <SectionHeader id="today" title={localToday === today ? 'Today' : formatDate(localToday)} action={<Link to={`/trips/${trip.id}`} className="text-sm font-medium text-forest-700 hover:underline">Full itinerary</Link>}>
            {effectiveMode === 'my' ? 'Your plans' : 'Everyone’s plans, including subgroup activities'}
          </SectionHeader>
          {b && todayFiltered.length === 0 ? (
            <Card className="p-4 text-sm text-muted">{active.phase === 'upcoming' ? `Your trip starts ${trip.start_date ? formatDate(trip.start_date) : 'soon'}.` : 'Nothing scheduled today. Enjoy the free time.'}</Card>
          ) : (
            <ol className="space-y-2">
              {todayFiltered.map((e) => <TimelineRow key={e.id} e={e} now={now} tz={tz} subgroupName={b?.subgroups.find((s) => s.id === e.subgroup_id)?.name} meId={session!.userId} />)}
            </ol>
          )}
        </section>

        <StartPoint />
        <p className="flex items-center gap-2 text-xs text-muted"><Wifi className="size-3.5" aria-hidden /> Last refreshed {new Date(bundle.dataUpdatedAt || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. Live transport status and routing are not connected; times shown are from your bookings.</p>
      </div>

      {/* Desktop context panel */}
      <aside className="hidden xl:block" aria-labelledby="attn-d">
        <div className="sticky top-6 space-y-3">
          <SectionHeader id="attn-d" title="Needs attention" />
          {b && <NowAttention bundle={b} memberIds={memberIds} mode={effectiveMode} />}
          <Card className="p-4">
            <p className="text-sm font-medium"><TripDates trip={trip} /></p>
            {b && b.stops.length > 0 && <p className="mt-1 text-sm text-muted">{[trip.origin_city, ...b.stops.map((s) => s.city)].filter(Boolean).join(' → ')}</p>}
          </Card>
        </div>
      </aside>
    </div>
  );
}

function NowAttention({ bundle, memberIds, mode }: { bundle: NonNullable<ReturnType<typeof useTrip>['data']>; memberIds: string[]; mode: DayMode }) {
  const navigate = useNavigate();
  const f = useFindings(bundle.trip, bundle.stops, bundle.events, memberIds, mode);
  if (f.loading) return <Spinner label="Checking" />;
  return <AttentionList tripId={bundle.trip.id} findings={f.visible} compact onResolve={(x) => navigate(`/trips/${bundle.trip.id}`, { state: { editEventId: x.eventIds[0], focusFinding: x.fingerprint } })} />;
}

function NextCard({ focus, inProgress, minutes, tripId, phase, needsTimeCount }: { focus: EventWithPeople | null; inProgress: boolean; minutes: number | null; tripId: string; phase: string; needsTimeCount: number }) {
  const navigate = useNavigate();
  if (!focus) {
    return (
      <Card className="p-5">
        <p className="text-sm text-muted">Next up</p>
        <p className="mt-1 font-display text-xl">{phase === 'undated' ? 'Add dates and bookings to see your next step' : 'No more timed plans'}</p>
        {needsTimeCount > 0 && <p className="mt-1 text-sm text-warn">{needsTimeCount} item{needsTimeCount > 1 ? 's' : ''} today {needsTimeCount > 1 ? 'have' : 'has'} no confirmed time.</p>}
        <Button className="mt-4" icon={<Plus className="size-4" />} onClick={() => navigate(`/trips/${tripId}`, { state: { addEvent: true } })}>Add to itinerary</Button>
      </Card>
    );
  }
  const action = primaryActionFor(focus);
  const fact = eventFactState(focus);
  const dest = focus.category === 'accommodation' ? focus.venue : focus.origin_name ?? focus.venue ?? focus.address;
  const start = (() => { try { return window.localStorage.getItem('elio-start-point') ?? ''; } catch { return ''; } })();
  const onAction = () => {
    if (!action) return;
    if (action.kind === 'directions' && dest) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}${start ? `&origin=${encodeURIComponent(start)}` : ''}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } else if (action.kind === 'open_documents') navigate('/documents', { state: { tripId } });
    else navigate(`/trips/${tripId}`, { state: { editEventId: focus.id } });
  };
  return (
    <Card className="overflow-hidden">
      <div className="flex items-start gap-4 p-5">
        <div className="hidden size-12 shrink-0 items-center justify-center rounded-2xl bg-forest-700 text-white min-[22rem]:flex"><CategoryIcon category={focus.category} className="size-6" /></div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-muted">{inProgress ? 'In progress' : minutes !== null ? (minutes < 1 ? 'Starting now' : `In ${formatDuration(minutes)}`) : 'Next up'}</p>
          <h2 className="font-display text-xl leading-snug break-anywhere">{focus.title}</h2>
          <p className="mt-1 text-[0.9375rem]"><EventTime e={focus} showDate /></p>
          {(focus.origin_name || focus.destination_name) && <p className="text-sm text-muted break-anywhere">{[focus.origin_name, focus.destination_name].filter(Boolean).join(' → ')}</p>}
          {!focus.origin_name && focus.venue && <p className="text-sm text-muted break-anywhere">{focus.venue}</p>}
          <div className="mt-2 flex flex-wrap gap-1.5">{fact && <FactBadge state={fact} />}{focus.is_fixed && <Badge>Fixed commitment</Badge>}</div>
        </div>
      </div>
      <div className="border-t border-line bg-ivory/60 px-5 py-3">
        <p className="flex items-start gap-2 text-sm text-muted"><CalendarClock className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span><strong className="font-medium text-ink">Leave-by time not calculated.</strong> Route estimates aren't connected yet, so we won't guess travel time or check-in cutoffs. Check the operator's guidance.</span>
        </p>
        {action && (
          <Button className="mt-3 w-full sm:w-auto" size="lg" onClick={onAction} icon={action.kind === 'directions' ? <Navigation className="size-4" /> : action.kind === 'confirm_time' ? <PenLine className="size-4" /> : <ArrowRight className="size-4" />}>
            {action.label}{action.kind === 'directions' && ' (opens maps)'}
          </Button>
        )}
      </div>
    </Card>
  );
}

function TimelineRow({ e, now, tz, subgroupName, meId }: { e: EventWithPeople; now: Date; tz: string; subgroupName?: string; meId: string }) {
  const past = e.end_utc ? new Date(e.end_utc) < now : e.start_utc ? new Date(e.start_utc) < now : false;
  const notMine = e.participant_ids.length > 0 && !e.participant_ids.includes(meId);
  return (
    <li className={cx('flex items-start gap-3 rounded-xl border border-line bg-white p-3', past && 'opacity-60')}>
      <div className="mt-0.5 text-forest-700"><CategoryIcon category={e.category} /></div>
      <div className="min-w-0 flex-1">
        <p className="font-medium leading-snug break-anywhere">{e.title}</p>
        <p className="text-sm text-muted"><EventTime e={e} viewerTz={tz} /></p>
        {(e.status === 'suggested' || subgroupName || notMine || past) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {e.status === 'suggested' && <Badge tone="info">Suggestion</Badge>}
            {subgroupName && <Badge tone="gold">{subgroupName}</Badge>}
            {notMine && <Badge>Not you</Badge>}
            {past && <Badge>Done</Badge>}
          </div>
        )}
      </div>
    </li>
  );
}

function AskInline({ tripTitle, onSubmit, onPhoto }: { tripTitle: string; onSubmit: (t: string) => void; onPhoto: () => void }) {
  const [text, setText] = useState('');
  const submit = (e: FormEvent) => { e.preventDefault(); if (text.trim()) onSubmit(text.trim()); };
  return (
    <form onSubmit={submit} className="flex items-center gap-2 rounded-2xl border border-line bg-white p-2 pl-3">
      <label htmlFor="ask-inline" className="sr-only">Ask about {tripTitle}</label>
      <Input id="ask-inline" className="border-0 px-1 focus:ring-0" placeholder={`Ask about ${tripTitle}…`} value={text} onChange={(e) => setText(e.target.value)} />
      <Button variant="ghost" aria-label="Ask with a photo or screenshot" onClick={onPhoto} className="px-3"><Camera className="size-5" /></Button>
      <Button type="submit" aria-label="Send" className="px-3" disabled={!text.trim()}><Send className="size-4" /></Button>
    </form>
  );
}

function TripPicker({ trips, activeId, overridden, onPick }: { trips: { id: string; title: string }[]; activeId: string; overridden: boolean; onPick: (id: string | null) => void }) {
  if (trips.length < 2 && !overridden) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label htmlFor="trip-pick" className="whitespace-nowrap text-sm text-muted">Following</label>
      <select id="trip-pick" className="input min-h-10 w-auto max-w-56 py-1 text-sm" value={overridden ? activeId : ''} onChange={(e) => onPick(e.target.value || null)}>
        <option value="">Automatic</option>
        {trips.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
      </select>
    </div>
  );
}

function StartPoint() {
  const [value, setValue] = useState(readStart);
  const [saved, setSaved] = useState(false);
  return (
    <Card className="p-4">
      <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); try { window.localStorage.setItem('elio-start-point', value.trim()); } catch { /* ignore */ } setSaved(true); }}>
        <div className="min-w-0 flex-[1_1_10rem]">
          <label htmlFor="start-point" className="field-label">Starting point for directions</label>
          <Input id="start-point" value={value} onChange={(e) => { setValue(e.target.value); setSaved(false); }} placeholder="e.g. your hotel or a station" />
          <p className="field-hint">Typed by you and kept on this device only. We don't track your location.</p>
        </div>
        <Button type="submit" variant="secondary">{saved ? 'Saved' : 'Save'}</Button>
      </form>
    </Card>
  );
}

function ChoiceCard({ to, icon, title, text }: { to: string; icon: React.ReactNode; title: string; text: string }) {
  return (
    <Link to={to} className="group rounded-2xl border border-line bg-white p-4 hover:border-forest-200 hover:shadow-sm">
      <span className="flex size-10 items-center justify-center rounded-xl bg-forest-50 text-forest-700">{icon}</span>
      <span className="mt-3 block font-medium">{title}</span>
      <span className="mt-0.5 block text-sm text-muted">{text}</span>
    </Link>
  );
}

function InvitationsBanner({ count }: { count: number }) {
  return (
    <Notice tone="info" title={`You have ${count} trip invitation${count > 1 ? 's' : ''}`} action={<Link to="/trips" className="text-sm font-medium text-forest-700 underline">Review in Trips</Link>}>
      Accepting adds the trip to your itinerary. Declining keeps your group membership unchanged.
    </Notice>
  );
}

